import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators,} from '@angular/forms';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {MatButtonModule} from '@angular/material/button';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatSelectModule} from '@angular/material/select';
import {MatSnackBar, MatSnackBarModule} from '@angular/material/snack-bar';
import {CdkDrag, CdkDragHandle, CdkDropList, moveItemInArray} from '@angular/cdk/drag-drop';
import {NgxSpinnerComponent, NgxSpinnerService} from 'ngx-spinner';
import {fuseAnimations} from '../../../../../@fuse/animations';
import {FuseCardComponent} from '../../../../../@fuse/components/card';
import {catchError, finalize, map, Observable, of, Subject, takeUntil, throwError} from 'rxjs';
import {RestFactoryService} from "../../../../core/services/rest-factory.service";
import {FuseConfirmationService} from '../../../../../@fuse/services/confirmation';

interface BannerImage {
    im_media: string | null;
    file?: File | null;
    ds_targetUrl: string | null;
    vi_order?: number;
    id_image?: number;
}

interface LanguageBannerData {
    youtubeUrl: string;
    images: BannerImage[];
}

interface PreviewItem {
    type: 'youtube' | 'image';
    url: string;
}

const defaultLanguages = [
    {code: 'en', label: 'English'}, {code: 'es', label: 'Spanish / Español'},
    {code: 'ar', label: 'Arabic / العربية'}, {code: 'ko', label: 'Korean / 한국어'},
    {code: 'hi', label: 'Hindi / हिंदी'}, {code: 'it', label: 'Italian / Italiano'},
    {code: 'ja', label: 'Japanese / 日本語'}, {code: 'bn', label: 'Bengali / বাংলা'},
    {code: 'ro', label: 'Romanian / Română'}, {code: 'cs', label: 'Czech / Čeština'},
    {code: 'pl', label: 'Polish / Polski'}, {code: 'zh', label: 'Chinese / 中文'},
    {code: 'vi', label: 'Vietnamese / Tiếng Việt'}, {code: 'fr', label: ' French / Français'},
    {code: 'de', label: 'German / Deutsch'},
];

@Component({
    selector: 'marketing-banners',
    templateUrl: './marketing-banners.component.html',
    styleUrls: ['./marketing-banners.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: fuseAnimations,
    standalone: true,
    imports: [
        CommonModule, ReactiveFormsModule, MatButtonModule, MatFormFieldModule,
        MatIconModule, MatInputModule, MatSelectModule, MatSnackBarModule,
        FuseCardComponent, NgxSpinnerComponent, CdkDropList, CdkDrag, CdkDragHandle
    ],
})
export class MarketingBannersComponent implements OnInit, OnDestroy {
    defaultLanguages = defaultLanguages;
    bannerForm: FormGroup;
    isLoading = false;
    activePreview: PreviewItem | null = null;
    safeYoutubeUrl: SafeResourceUrl | null = null;

    private successfullyLoadedLanguage: string;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _fb: FormBuilder,
        private _cdk: ChangeDetectorRef,
        private _spinner: NgxSpinnerService,
        private _snackBar: MatSnackBar,
        private _restFactoryService: RestFactoryService,
        private _sanitizer: DomSanitizer,
        private _fuseConfirmationService: FuseConfirmationService,
    ) {
    }

    ngOnInit(): void {
        this.initForm();
        this.successfullyLoadedLanguage = 'en';
        this.loadBannerDataForLanguage('en');
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    get bannerImages(): FormArray {
        return this.bannerForm.get('images') as FormArray;
    }

    get safePreviewUrl(): SafeResourceUrl | null {
        if (this.activePreview?.type === 'youtube') {
            return this._sanitizer.bypassSecurityTrustResourceUrl(this.activePreview.url);
        }
        return null;
    }

    private initForm(): void {
        this.bannerForm = this._fb.group({
            language: ['en', Validators.required],
            youtubeUrl: ['', [
                Validators.required,
                Validators.pattern(/^(?:https?:\/\/)?(?:www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/)
            ]],
            images: this._fb.array([]),
        });

        this.bannerForm.get('language').valueChanges
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((selectedLangCode) => {
                if (selectedLangCode === this.successfullyLoadedLanguage) {
                    return;
                }

                if (this.bannerForm.dirty) {
                    this.confirmLanguageChange(selectedLangCode);
                } else {
                    this.loadBannerDataForLanguage(selectedLangCode);
                }
            });
    }

    private confirmLanguageChange(newLangCode: string): void {
        const confirmation = this._fuseConfirmationService.open({
            title: 'Unsaved Changes',
            message: 'You have unsaved changes that will be lost. Are you sure you want to switch languages?',
            actions: {
                confirm: {
                    label: 'Switch & Discard',
                    color: 'warn',
                },
            },
        });

        confirmation.afterClosed().subscribe((result) => {
            if (result === 'confirmed') {
                this.loadBannerDataForLanguage(newLangCode);
            } else {
                this.bannerForm.get('language').setValue(this.successfullyLoadedLanguage, {emitEvent: false});
                this._cdk.markForCheck();
            }
        });
    }

    private createImageGroup(data: BannerImage): FormGroup {
        return this._fb.group({
            previewUrl: [data.im_media, Validators.required],
            file: [data.file],
            targetUrl: [data.ds_targetUrl, Validators.pattern(/^(https|http):\/\/[^\s$.?#].[^\s]*$/)],
        });
    }

    private loadBannerDataForLanguage(langCode: string): void {
        this.isLoading = true;
        this._spinner.show().then();
        this.activePreview = null;

        this.bannerForm.get('youtubeUrl').reset('', {emitEvent: false});
        this.bannerImages.clear({emitEvent: false});

        const payload = new FormData();
        payload.append("id_language", langCode);

        this._restFactoryService.httpPostService("/admin/marketing/banners/get", payload)
            .pipe(
                takeUntil(this._unsubscribeAll),
                finalize(() => {
                    this.isLoading = false;
                    this._spinner.hide().then();
                    this.bannerForm.markAsPristine();
                    this.setPreview('youtube');
                    this._cdk.markForCheck();
                }),
                catchError((err) => {
                    console.error("Error fetching banner data:", err);
                    this.successfullyLoadedLanguage = langCode;
                    return of(null);
                })
            )
            .subscribe((res) => {
                this.successfullyLoadedLanguage = langCode;

                if (res && res.js_value) {
                    const data = res.js_value;
                    this.bannerForm.patchValue({youtubeUrl: data.ds_video}, {emitEvent: false});

                    if (data.ar_banners && data.ar_banners.length > 0) {
                        data.ar_banners.forEach(banner => {
                            this.bannerImages.push(
                                this.createImageGroup({
                                    ds_targetUrl: banner.ds_targetUrl,
                                    im_media: banner.im_media,
                                    vi_order: banner.vi_order,
                                    id_image: banner.id_image,
                                    file: null,
                                })
                            );
                        });
                    }
                }
            });
    }

    addBanner(): void {
        this.bannerImages.push(this.createImageGroup({
            ds_targetUrl: null,
            im_media: null,
            vi_order: null,
            id_image: null,
            file: null
        }));
        this.bannerForm.markAsDirty();
    }

    drop(event: any): void {
        moveItemInArray(this.bannerImages.controls, event.previousIndex, event.currentIndex);
        this.bannerForm.markAsDirty();
    }

    onFileSelected(event: Event, index: number): void {
        const input = event.target as HTMLInputElement;
        if (!input.files || !input.files.length) {
            return;
        }

        const file = input.files[0];
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            this._snackBar.open('Invalid file type.', 'Close', {duration: 3000});
            return;
        }

        this.isLoading = true;
        this._spinner.show().then();

        this.uploadImage(file).pipe(
            finalize(() => {
                this.isLoading = false;
                this._spinner.hide().then();
                input.value = '';
            })
        ).subscribe({
            next: (mediaUrl) => {
                const imageControl = this.bannerImages.at(index) as FormGroup;
                imageControl.patchValue({
                    previewUrl: mediaUrl,
                    file: null,
                });
                this.bannerForm.markAsDirty();
                this.setPreview('image', index);
                this._cdk.markForCheck();
                this._snackBar.open('Image uploaded.', 'OK', {duration: 2000});
            },
            error: (err) => {
                this._snackBar.open(err.message, 'Close', {duration: 3000});
                this._cdk.markForCheck();
            }
        });
    }

    removeImage(index: number): void {
        const imageControl = this.bannerImages.at(index) as FormGroup;
        if (this.activePreview?.type === 'image' && this.activePreview.url === imageControl.value.previewUrl) {
            this.setPreview('youtube');
        }
        this.bannerImages.removeAt(index);
        this.bannerForm.markAsDirty();
    }

    setPreview(type: 'youtube' | 'image', index?: number): void {
        this.activePreview = null;
        this.safeYoutubeUrl = null;

        if (type === 'youtube') {
            const url = this.bannerForm.get('youtubeUrl').value;
            const videoId = this.getYouTubeVideoId(url);
            if (videoId) {
                const embedUrl = `https://www.youtube.com/embed/${videoId}`;
                this.activePreview = {type: 'youtube', url: embedUrl};
                this.safeYoutubeUrl = this._sanitizer.bypassSecurityTrustResourceUrl(embedUrl);
            }
        } else if (type === 'image' && index !== undefined) {
            const imageUrl = this.bannerImages.at(index)?.get('previewUrl')?.value;
            if (imageUrl) {
                this.activePreview = {type: 'image', url: imageUrl};
            }
        }
        this._cdk.markForCheck();
    }

    private getYouTubeVideoId(url: string): string | null {
        if (!url) return null;
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
        const match = url.match(regExp);
        return match && match[2].length === 11 ? match[2] : null;
    }

    private uploadImage(file: File): Observable<string> {
        const payload = new FormData();
        payload.append('im_image', file, file.name);

        return this._restFactoryService.httpPostService('/admin/marketing/banners/imageadd', payload)
            .pipe(
                map(res => {
                    if (res && res.im_image) {
                        return res.im_image;
                    }
                    throw new Error('Invalid response from image server.');
                }),
                catchError(err => {
                    console.error('Image upload error:', err);
                    return throwError(() => new Error('Image upload failed. Please try again.'));
                })
            );
    }

    saveBanners(): void {
        if (this.bannerForm.invalid) {
            this.bannerForm.markAllAsTouched();
            this._cdk.markForCheck();
            this._snackBar.open('Please correct the errors. A valid YouTube URL is required and each banner must have an image.', 'Close', {duration: 4000});
            return;
        }

        this.isLoading = true;
        this._spinner.show().then();

        const formData = this.bannerForm.getRawValue();
        const apiPayloadObject = {
            ds_video: formData.youtubeUrl,
            ar_banners: formData.images
                .map((img, index) => ({
                    im_media: img.previewUrl,
                    ds_targetUrl: img.targetUrl,
                    vi_order: index + 1
                }))
        };

        const payload = new FormData();
        payload.append("id_language", formData.language);
        payload.append("js_value", JSON.stringify(apiPayloadObject));

        this._restFactoryService.httpPostService("/admin/marketing/banners/set", payload)
            .pipe(
                takeUntil(this._unsubscribeAll),
                finalize(() => {
                    this.isLoading = false;
                    this._spinner.hide().then();
                    this._cdk.markForCheck();
                }),
                catchError((err) => {
                    console.error("Error saving banner data:", err);
                    this._snackBar.open('Failed to save banners. Please try again.', 'Close', {duration: 3000});
                    return of(null);
                })
            )
            .subscribe((res) => {
                if (res && res.vi_status === 0) {
                    this._snackBar.open('Banners saved successfully!', 'OK', {duration: 3000});
                    this.bannerForm.markAsPristine();
                } else {
                    const errorMessage = res?.ds_Status || 'An unknown error occurred.';
                    this._snackBar.open(`Save failed: ${errorMessage}`, 'Close', {duration: 5000});
                }
            });
    }
}
